import Link from "next/link";
import type { DefaultValues } from "react-hook-form";
import type { CompanyContactFormDataT } from "@/app/schemas/zod/contact-form-schema";
import type {
  ContactFormConfigT,
  ContactFormFieldConfigT,
} from "./contact-form-types";

export type CompanyContactFormFieldNameT =
  | "salutation"
  | "firstname"
  | "lastname"
  | "email"
  | "phone"
  | "comments"
  | "privacy"
  | "mail"
  | "case"
  | "URL"
  | "source"
  | "dealerdesk"
  | "vehicleCondition"
  | "company";

export const companyContactFormDefaultValues: DefaultValues<CompanyContactFormDataT> =
  {
    salutation: undefined,
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    comments: undefined,
    privacy: false,
    mail: "",
    case: "OPPORTUNITY",
    URL: undefined,
    source: "Firmen Anfrage",
    dealerdesk: "Firmenangebot",
    vehicleCondition: undefined,
    company: undefined,
  };

const salutationField: ContactFormFieldConfigT<CompanyContactFormFieldNameT> = {
  name: "salutation",
  type: "select",
  label: "Anrede",
  placeholder: "Bitte auswählen...",
  options: [
    { value: "Herr", label: "Herr" },
    { value: "Frau", label: "Frau" },
    { value: "Divers", label: "Divers" },
  ],
};

const nameFields: ContactFormFieldConfigT<CompanyContactFormFieldNameT>[] = [
  {
    name: "firstname",
    type: "name",
    label: "Vorname",
    placeholder: "Vorname",
  },
  {
    name: "lastname",
    type: "name",
    label: "Name",
    placeholder: "Nachname",
  },
];

const contactFields: ContactFormFieldConfigT<CompanyContactFormFieldNameT>[] = [
  {
    name: "email",
    type: "text",
    inputType: "email",
    label: "E-Mail",
    placeholder: "E-Mail",
  },
  {
    name: "phone",
    type: "phone",
    label: "Telefon",
    placeholder: "+49 (123) 45678902",
  },
];

const commentsField: ContactFormFieldConfigT<CompanyContactFormFieldNameT> = {
  name: "comments",
  type: "textarea",
  label: "Ihre Nachricht",
  placeholder: "Ihre Nachricht...",
  colSpan: 2,
};

const privacyField: ContactFormFieldConfigT<CompanyContactFormFieldNameT> = {
  name: "privacy",
  type: "checkbox",
  colSpan: 2,
  label: (
    <span className="text-sm leading-relaxed whitespace-normal sm:whitespace-nowrap">
      Mit absenden bestätigen Sie unsere{" "}
      <Link href="/datenschutz" className="underline">
        Datenschutzerklärung
      </Link>
      .
    </span>
  ),
};

const honeypotField: ContactFormFieldConfigT<CompanyContactFormFieldNameT> = {
  name: "mail",
  type: "honeypot",
};

const caseField: ContactFormFieldConfigT<CompanyContactFormFieldNameT> = {
  name: "case",
  type: "select",
  hidden: true,
  options: [
    { value: "CASE", label: "CASE" },
    { value: "OPPORTUNITY", label: "OPPORTUNITY" },
  ],
};

const urlField: ContactFormFieldConfigT<CompanyContactFormFieldNameT> = {
  name: "URL",
  type: "text",
  hidden: true,
};

const vehicleConditionField: ContactFormFieldConfigT<CompanyContactFormFieldNameT> =
  {
    name: "vehicleCondition",
    type: "text",
    hidden: true,
  };

const companyField: ContactFormFieldConfigT<CompanyContactFormFieldNameT> = {
  name: "company",
  type: "text",
  hidden: true,
};

export const companyContactFormConfig: ContactFormConfigT<CompanyContactFormFieldNameT> =
  {
    sections: [
      {
        id: "salutation",
        layout: "grid",
        columns: 1,
        fields: [salutationField],
      },
      {
        id: "primary",
        layout: "grid",
        columns: 2,
        fields: [
          ...nameFields,
          ...contactFields,
          honeypotField,
          caseField,
          urlField,
          vehicleConditionField,
          companyField,
        ],
      },
      {
        id: "additional",
        layout: "grid",
        columns: 2,
        fields: [commentsField, privacyField],
      },
    ],
    honeypotFieldName: "mail",
    submitButtonLabel: "Nachricht senden",
    submittingLabel: "Wird gesendet...",
    successToast: {
      title: "Nachricht erfolgreich gesendet",
      description: "Wir werden uns in Kürze bei Ihnen melden.",
    },
    errorToast: {
      title: "Fehler beim Senden",
      description:
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
    },
  };
